// @ts-strict-ignore
import * as React from 'react';
import styles from '../styles/stepStyles';
import { ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';

import {
    AmazonAppStoreData,
    AppleAppStoreData,
    AppleAppStoreDefaultSettings,
    AppStoreAvailableBuild,
    AppStoreState,
    AppStoreType,
    AppStoreBuildSubmissionStatus,
    AutomationAutoSubmitRelease,
    AutomationType,
    AutoSubmitStrategy,
    GoogleAppStoreSettings,
    GooglePlayStoreData,
    HuaweiAppGallerySettings,
    HuaweiAppStoreData,
    ReleaseStepStatus,
    ReleaseStepSubmission,
    ReleaseStepSubmissionData,
    ReleaseStepType,
    SamsungAppStoreData,
    RunwayResponseType,
} from '../../types';
import { FeatureSection } from '../../typescriptHelpers';
import { runwayColors } from '../styles/globalStyles';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import WorkflowBuildModule from '../workflowModule/WorkflowBuildModule';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import AppStoreSubmitConfirmationModal from '../AppStoreSubmitConfirmationModal';
import EditAppleAppStoreReleaseSettings from '../EditAppleAppStoreReleaseSettings';
import EditAppleAppReleaseDefaults from '../EditAppleAppReleaseDefaults';
import EditGoogleAppReleaseDefaults from '../EditGoogleAppReleaseDefaults';
import { iconForPlatform } from '../../helpers/platformComponentHelpers';
import SubmissionModal from '../SubmissionModal';
import * as AnalyticEvents from '../../utils/analytics';
import TrackedButton from '../../helpers/TrackedButton';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';

import { ReleaseEventsTimelineSection } from '../releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import Popconfirm from 'antd/lib/popconfirm';
import EditGoogleReleaseSettingsContainer from '../appSettings/EditGoogleReleaseSettingsContainer';
import {
    appleAppCanBeDeveloperRejected,
    appStoreTitleForState,
    getAutomationIdByType,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    innerStepStatusColorForReleaseStepStatus,
    isAppReleased,
    isAppSubmitted,
} from '@/helpers';
import { displayConsoleNameForAppStoreType } from '@/helpers/displayHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { ReleaseUpdatable } from '@/actions/updatable';
import { RootState } from '@/reducers/reducers';
import {
    selectApp,
    selectAppStoreType,
    selectAutomationById,
    selectChecklistItemsForReleaseStep,
    selectFirstAutomationOfType,
    selectIsReleaseRollback,
    selectIsWorkflowsMigratedAndEnabled,
    selectRelease,
    selectReleaseStep,
    selectStepRun,
    selectSubmissionIsRunwayAutomated,
    selectSubmissionSuccessfullyRunwayAutomated,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import { BaseUpdateReleaseRequest, tryUpdateRelease } from '@/actions/release';
import { globalMessage } from '@/utils/globalMessage';
import { StepContentSubmissionIOS } from '@/components/StepContentSubmission/StepContentSubmissionIOS';
import { BuildAndReviewSettingsCard } from '@/components/AppStoreItems/BuildAndReviewSettingsCard';
import SelectedAppStoreBuildModule from '@/components/SelectedAppStoreBuildModule';
import EditHuaweiReleaseSettingsContainer from '@/components/appSettings/EditHuaweiReleaseSettingsContainer';
import EditHuaweiAppReleaseDefaults from '../EditHuaweiAppReleaseDefaults';
import { useError, useIsLoading, useUpdated, useCallUpdatable } from '@/utils/hooks/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { StepRunUpdatable } from '@/actions/stepRun';

export const StepContentSubmission: React.FC<ReleaseStepOrStepRunComponentProps> = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
}) => {
    const app = useSelector((state: RootState) => selectApp(state, appId));
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const isRollback = useSelector((state: RootState) => selectIsReleaseRollback(state, releaseId));
    const releaseStep = useSelector((state: RootState) =>
        selectReleaseStep(state, releaseId, releaseStepType),
    ) as ReleaseStepSubmission;

    // Get release stepData if stepRun
    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: ReleaseStepSubmissionData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;

    const autoStagedRolloutAutomationId = useSelector((state: RootState) =>
        stepRun ?
            stepData.autoStagedRolloutAutomationId
        :   selectFirstAutomationOfType(state, appId, AutomationType.AutoStagedRollout)?.id,
    );

    const submissionCLIs =
        stepRun ?
            stepRun?.checklistItems
        :   useSelector((state: RootState) =>
                selectChecklistItemsForReleaseStep(state, releaseId, ReleaseStepType.Submission),
            );

    const appStoreType =
        stepRun ? stepData.appStoreType : useSelector((state: RootState) => selectAppStoreType(state, appId));
    const platform = stepRun ? stepData.platform : app.appSettings.platform;
    const storeUrl = stepData.storeUrl;
    const stepStatus = stepRun ? stepRun.status : releaseStep.status;
    const prodWorkflowData = stepData.workflowData;
    const prodBuild = stepData.prodBuild;
    const selectedAppStoreBuilds = stepData.appStoreSelectedBuilds ?? [];
    const displayableVersion = release.displayableVersion;

    const automationRuns = stepRun ? stepRun.automationRuns : releaseStep.automationRuns;

    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    const appStoreReleaseSettings = stepData.appStoreReleaseSettings;

    const appStoreData = stepData.appStoreData as AppleAppStoreData;
    const googleStoreData = stepData.appStoreData as GooglePlayStoreData;
    const amazonData = stepData.appStoreData as AmazonAppStoreData;
    const huaweiData = stepData.appStoreData as HuaweiAppStoreData;
    const samsungData = stepData.appStoreData as SamsungAppStoreData;

    const appleAppStoreState = appStoreData?.versionReviewSubmissionItem?.state;
    const googleAppStoreState = googleStoreData?.state;
    const amazonAppStoreState = amazonData?.state;
    const huaweiAppStoreState = huaweiData?.state;
    const samsungAppStoreState = samsungData?.state;

    let appStoreState: AppStoreState | undefined;
    if (appleAppStoreState) {
        appStoreState = appleAppStoreState;
    } else if (googleAppStoreState) {
        appStoreState = googleAppStoreState;
    } else if (amazonAppStoreState) {
        appStoreState = amazonAppStoreState;
    } else if (huaweiAppStoreState) {
        appStoreState = huaweiAppStoreState;
    } else if (samsungAppStoreState) {
        appStoreState = samsungAppStoreState;
    }

    const lastCompletedRelease = stepData.lastCompletedRelease;

    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);

    const isSubmitted = isAppSubmitted(
        appStoreType,
        appStoreData?.versionReviewSubmissionItem?.state,
        appStoreData?.relevantReviewSubmission?.state,
        googleStoreData?.state,
        amazonData?.state,
        huaweiData?.state,
        samsungData?.state,
    );
    const isSubmittedByRunway = useSelector((state: RootState) =>
        selectSubmissionSuccessfullyRunwayAutomated(state, appId, releaseId),
    );
    const willBeSubmittedByRunway = useSelector((state: RootState) =>
        selectSubmissionIsRunwayAutomated(state, appId, releaseId),
    );

    let autoSubmitAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, getAutomationIdByType(automationRuns, AutomationType.AutoSubmitRelease)),
    ) as AutomationAutoSubmitRelease;

    const autoSubmitStrategy = autoSubmitAutomation?.data?.submitStrategy;

    const isReleased = isAppReleased(
        appStoreType,
        appStoreData?.versionReviewSubmissionItem?.state,
        googleStoreData?.state,
        amazonData?.state,
        huaweiData?.state,
        samsungData?.state,
    );

    const appCanBeDeveloperRejected =
        appStoreData ? appleAppCanBeDeveloperRejected(appStoreData?.versionReviewSubmissionItem?.state) : false;

    const buildSubmissionStatus = appStoreData?.relevantReviewSubmission?.buildSubmissionStatus;
    const hasBlockingSubmissionState =
        buildSubmissionStatus === AppStoreBuildSubmissionStatus.Blocked ||
        buildSubmissionStatus === AppStoreBuildSubmissionStatus.UnresolvedIssues;
    const [
        rejectAppleSubmissionStepRun,
        rejectAppleSubmissionIsLoadingStepRun,
        rejectAppleSubmissionResponseTypeStepRun,
        rejectAppleSubmissionErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.RejectAppleSubmission, stepRunComponentProps);

    const rejectAppleSubmissionIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.RejectAppleSubmission, releaseId);
    const rejectAppleSubmissionSuccessReleaseStep = useUpdated(ReleaseUpdatable.RejectAppleSubmission, releaseId);
    const rejectAppleSubmissionErrorReleaseStep = useError(ReleaseUpdatable.RejectAppleSubmission, releaseId);

    const rejectAppleSubmissionIsLoading =
        stepRunComponentProps ? rejectAppleSubmissionIsLoadingStepRun : rejectAppleSubmissionIsLoadingReleaseStep;
    const rejectAppleSubmissionSuccess =
        stepRunComponentProps ?
            rejectAppleSubmissionResponseTypeStepRun === RunwayResponseType.Success
        :   rejectAppleSubmissionSuccessReleaseStep;
    const rejectAppleSubmissionError =
        stepRunComponentProps ? rejectAppleSubmissionErrorStepRun : rejectAppleSubmissionErrorReleaseStep;

    const isWorkflowsMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsMigratedAndEnabled(state, appId),
    );

    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

    const isSRREnabled =
        workflowEnabledSteps[ReleaseStepType.Submission] &&
        workflowEnabledSteps[ReleaseStepType.StoreReview] &&
        workflowEnabledSteps[ReleaseStepType.Takeoff];

    const shouldHideRunProdWorkflowButton = isWorkflowsMigratedAndEnabled && isSRREnabled;

    React.useEffect(() => {
        if (rejectAppleSubmissionSuccess) {
            globalMessage.success('Successfully rejected your submission.');
        }
    }, [rejectAppleSubmissionSuccess]);

    React.useEffect(() => {
        if (rejectAppleSubmissionError) {
            globalMessage.error(rejectAppleSubmissionError);
        }
    }, [rejectAppleSubmissionError]);

    const selectedBuildNumbers = selectedAppStoreBuilds.map((build) => `#${build.appStoreBuild.buildNumber}`);
    const isRollbackReadyToSubmitState = isRollback && stepStatus === ReleaseStepStatus.Pending;
    const requiresSelectedBuildsForSubmission = appStoreType !== AppStoreType.Huawei && !isRollbackReadyToSubmitState;
    const disableSubmitButton =
        (requiresSelectedBuildsForSubmission && selectedAppStoreBuilds.length === 0) ||
        isSubmitted ||
        hasBlockingSubmissionState;
    const showDeveloperRejectButton = isAppleAppStore && isSubmitted && !isReleased;

    const [submitModalVisible, setSubmitModalVisible] = React.useState(false);
    const [submitInProgressModalVisible, setSubmitInProgressModalVisible] = React.useState(false);
    const [editReleaseSettingsModalVisible, setEditReleaseSettingsModalVisible] = React.useState(false);
    const [editReleaseDefaultsModalVisible, setEditReleaseDefaultsModalVisible] = React.useState(false);
    const [releasePreviousSubmissionToAllUsersFirst, setReleasePreviousSubmissionToAllUsersFirst] =
        React.useState(false);
    const [releaseBetaTrackToFullPercentagePriorToSubmission, setReleaseBetaTrackToFullPercentagePriorToSubmission] =
        React.useState(false);
    const [shouldMatchPreviousPhasedRelease, setShouldMatchPreviousPhasedRelease] = React.useState(false);

    const dispatch = useDispatch();
    const rejectAppleSubmission = () => {
        if (stepRunComponentProps) {
            rejectAppleSubmissionStepRun();
        } else {
            const request: BaseUpdateReleaseRequest = {
                orgId,
                appId,
                releaseId,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.RejectAppleSubmission, request, request.releaseId));
        }
    };

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    ...styles.contentWithFooterStyle,
                    paddingTop: 0,
                }}
            >
                <div style={{ display: 'flex', marginBottom: 30, justifyContent: 'space-between', gap: 12 }}>
                    <EmptyStateWrapper
                        releaseStepType={releaseStepType}
                        appId={appId}
                        releaseId={releaseId}
                        featureSections={[]}
                        shouldForceDisable
                    >
                        {(isReleaseFromScraper) => {
                            return (
                                <div
                                    style={{
                                        maxWidth: '50%',
                                        paddingTop: 28,
                                    }}
                                >
                                    <Space direction="vertical" size={4}>
                                        <Text
                                            style={{
                                                fontSize: 30,
                                                color: statusColorForReleaseStepStatus(
                                                    stepStatus,
                                                    isReleaseFromScraper,
                                                ),
                                                fontWeight: 600,
                                            }}
                                        >
                                            {titleForState(
                                                isSubmitted,
                                                appStoreType,
                                                stepStatus,
                                                appStoreState,
                                                isReleaseFromScraper,
                                                selectedAppStoreBuilds.length > 0,
                                            )}
                                        </Text>
                                        <Text type="secondary">
                                            {descriptionForState(
                                                isSubmitted,
                                                isSubmittedByRunway,
                                                willBeSubmittedByRunway,
                                                autoSubmitStrategy,
                                                stepStatus,
                                                isReleaseFromScraper,
                                                isRollback,
                                                selectedBuildNumbers,
                                                hasBlockingSubmissionState,
                                                displayableVersion,
                                            )}
                                        </Text>
                                    </Space>
                                </div>
                            );
                        }}
                    </EmptyStateWrapper>
                    <ReleaseEventsTimelineSection
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        stepRunId={stepRunComponentProps?.stepRunId}
                        context={ReleaseTimelineSectionContext.ReleaseStep}
                        releaseStepType={releaseStepType}
                    />
                </div>
                {!shouldHideRunProdWorkflowButton && prodWorkflowData && (
                    <WorkflowBuildModule
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                        releaseStepStatus={stepStatus}
                        platform={platform}
                        workflowData={prodWorkflowData}
                        buildInfo={prodBuild}
                        autoUploadDsymsToStabilityMonitoringAutomationId={
                            autoUploadDsymsToStabilityMonitoringAutomationId
                        }
                        autoUploadBinaryArtifactToAppStoreAutomationId={autoUploadBinaryArtifactToAppStoreAutomationId}
                        autoDownloadArtifactsAutomationId={autoDownloadArtifactsAutomationId}
                        stepRunComponentProps={stepRunComponentProps}
                    />
                )}

                {isAppleAppStore ?
                    <StepContentSubmissionIOS
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseStepType={releaseStepType}
                        releaseId={releaseId}
                        isRollback={isRollback}
                        setEditReleaseSettingsModalVisible={setEditReleaseSettingsModalVisible}
                        isReleased={isReleased}
                        storeUrl={storeUrl}
                        appStoreType={appStoreType}
                        platform={platform}
                        displayableVersion={displayableVersion}
                        lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                        regressionBuildInfo={stepData.regressionBuildInfo}
                        regressionStatus={stepData.regressionStatus}
                        {...stepData}
                        appStoreData={appStoreData}
                        appStoreReleaseSettings={appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                        stepStatus={stepStatus}
                    />
                : isGooglePlayStore ?
                    <BuildAndReviewSettingsCard
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                        googleStoreData={googleStoreData}
                        huaweiStoreData={huaweiData}
                        samsungStoreData={samsungData}
                        appStoreType={appStoreType}
                        platform={platform}
                        appStoreReleaseSettings={appStoreReleaseSettings}
                        isRollback={isRollback}
                        onShowAppStoreReleaseSettings={() => setEditReleaseSettingsModalVisible(true)}
                        storeUrl={storeUrl}
                        isReleased={isReleased}
                        lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                        regressionBuildInfo={stepData.regressionBuildInfo}
                        regressionStatus={stepData.regressionStatus}
                        {...stepData}
                        appStoreData={appStoreData}
                    />
                : isHuaweiAppGallery ?
                    <BuildAndReviewSettingsCard
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        releaseStepType={releaseStepType}
                        googleStoreData={googleStoreData}
                        huaweiStoreData={huaweiData}
                        samsungStoreData={samsungData}
                        appStoreType={appStoreType}
                        platform={platform}
                        appStoreReleaseSettings={appStoreReleaseSettings}
                        isRollback={isRollback}
                        onShowAppStoreReleaseSettings={() => setEditReleaseSettingsModalVisible(true)}
                        storeUrl={storeUrl}
                        isReleased={isReleased}
                        lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                        {...stepData}
                        appStoreData={appStoreData}
                    />
                :   <EmptyStateWrapper
                        releaseStepType={releaseStepType}
                        appId={appId}
                        releaseId={releaseId}
                        featureSections={[FeatureSection.SubmissionSelectedBuildModule]}
                    >
                        <SelectedAppStoreBuildModule
                            stepRunComponentProps={stepRunComponentProps}
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            releaseStepType={releaseStepType}
                            platform={platform}
                            appStoreType={appStoreType}
                            appStoreState={
                                appStoreData?.versionReviewSubmissionItem?.state ?? googleStoreData?.state ?? null
                            }
                            reviewSubmissionState={appStoreData?.relevantReviewSubmission?.state}
                            showChangeSelectedBuildCTA
                            regressionBuildInfo={stepData.regressionBuildInfo}
                            regressionStatus={stepData.regressionStatus}
                            stepStatus={stepStatus}
                            {...stepData}
                        />
                    </EmptyStateWrapper>
                }
            </Content>
            <Footer style={styles.footerStyle}>
                <Space>
                    <TrackedButton
                        eventName={AnalyticEvents.Event.ClickAppStoreConsoleButton}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                        context={AnalyticEvents.Context.ReleaseStep}
                        href={storeUrl}
                        icon={<ArrowRightOutlined style={{ color: runwayColors.info }} />}
                        target="_blank"
                        disabled={isReleased}
                    >
                        {appStoreButtonText(appStoreType)}
                    </TrackedButton>
                    {showDeveloperRejectButton ?
                        <Popconfirm
                            title="Are you sure you want to reject this build?"
                            okText="Yes, reject"
                            okButtonProps={{ danger: true }}
                            onConfirm={rejectAppleSubmission}
                        >
                            <PermissionedActionButton
                                releaseId={releaseId}
                                actionType={RunwayUserAction.UADevRejectAppUpdate}
                                icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />}
                                loading={rejectAppleSubmissionIsLoading}
                                disabled={!appCanBeDeveloperRejected}
                            >
                                Pull from review
                            </PermissionedActionButton>
                        </Popconfirm>
                    :   <PermissionedActionButton
                            releaseId={releaseId}
                            actionType={RunwayUserAction.UASubmitAppUpdate}
                            icon={iconForPlatform(platform, runwayColors.white)}
                            disabled={disableSubmitButton}
                            type="primary"
                            onClick={() => setSubmitModalVisible(true)}
                        >
                            {submitText(selectedAppStoreBuilds)}
                        </PermissionedActionButton>
                    }
                </Space>
            </Footer>
            <AppStoreSubmitConfirmationModal
                stepRunComponentProps={stepRunComponentProps}
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                releaseStepType={releaseStepType}
                displayableVersion={displayableVersion}
                platform={platform}
                appStoreType={appStoreType}
                open={submitModalVisible}
                lastCompletedRelease={lastCompletedRelease}
                autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                latestReleasePhasedRelease={stepData?.latestReleasePhasedRelease}
                submissionCLIs={submissionCLIs}
                onSubmit={(
                    releasePreviousSubmissionToAllUsersFirst,
                    releaseBetaTrackToFullPercentagePriorToSubmission,
                    shouldMatchPreviousPhasedRelease,
                ) => {
                    setSubmitModalVisible(false);
                    setSubmitInProgressModalVisible(true);
                    setReleasePreviousSubmissionToAllUsersFirst(releasePreviousSubmissionToAllUsersFirst);
                    setReleaseBetaTrackToFullPercentagePriorToSubmission(
                        releaseBetaTrackToFullPercentagePriorToSubmission,
                    );
                    setShouldMatchPreviousPhasedRelease(shouldMatchPreviousPhasedRelease);
                }}
                onClose={() => setSubmitModalVisible(false)}
                onShowReleaseSettingsModal={() => {
                    setSubmitModalVisible(false);
                    setEditReleaseSettingsModalVisible(true);
                }}
                onShowReleaseDefaultsModal={() => {
                    setSubmitModalVisible(false);
                    setEditReleaseDefaultsModalVisible(true);
                }}
                {...stepData}
            />
            {isAppleAppStore && (
                <>
                    <EditAppleAppStoreReleaseSettings
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        displayableVersion={displayableVersion}
                        open={editReleaseSettingsModalVisible}
                        appStoreReleaseSettings={appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                        appStoreData={appStoreData}
                        isRollback={isRollback}
                        onClose={() => setEditReleaseSettingsModalVisible(false)}
                        onSuccess={() => setEditReleaseSettingsModalVisible(false)}
                    />
                    <EditAppleAppReleaseDefaults
                        orgId={orgId}
                        appId={appId}
                        open={editReleaseDefaultsModalVisible}
                        onClose={() => setEditReleaseDefaultsModalVisible(false)}
                        onSuccess={() => setEditReleaseDefaultsModalVisible(false)}
                    />
                </>
            )}
            {isGooglePlayStore && (
                <>
                    <EditGoogleReleaseSettingsContainer
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        open={editReleaseSettingsModalVisible}
                        releaseSettings={appStoreReleaseSettings as GoogleAppStoreSettings}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        isReleased={isReleased}
                        onClose={() => setEditReleaseSettingsModalVisible(false)}
                        onSuccess={() => setEditReleaseSettingsModalVisible(false)}
                    />
                    <EditGoogleAppReleaseDefaults
                        orgId={orgId}
                        appId={appId}
                        open={editReleaseDefaultsModalVisible}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        onClose={() => setEditReleaseDefaultsModalVisible(false)}
                        onSuccess={() => setEditReleaseDefaultsModalVisible(false)}
                    />
                </>
            )}
            {isHuaweiAppGallery && (
                <>
                    <EditHuaweiReleaseSettingsContainer
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        open={editReleaseSettingsModalVisible}
                        releaseSettings={appStoreReleaseSettings as HuaweiAppGallerySettings}
                        onClose={() => setEditReleaseSettingsModalVisible(false)}
                        onSuccess={() => setEditReleaseSettingsModalVisible(false)}
                    />
                    <EditHuaweiAppReleaseDefaults
                        orgId={orgId}
                        appId={appId}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        open={editReleaseDefaultsModalVisible}
                        onClose={() => setEditReleaseDefaultsModalVisible(false)}
                        onSuccess={() => setEditReleaseDefaultsModalVisible(false)}
                    />
                </>
            )}
            {selectedAppStoreBuilds && (
                <SubmissionModal
                    orgId={orgId}
                    appId={appId}
                    stepRunComponentProps={stepRunComponentProps}
                    releaseId={releaseId}
                    displayableVersion={displayableVersion}
                    platform={platform}
                    appStoreType={appStoreType}
                    releasePreviousSubmissionToAllUsersFirst={releasePreviousSubmissionToAllUsersFirst}
                    releaseBetaTrackToFullPercentagePriorToSubmission={
                        releaseBetaTrackToFullPercentagePriorToSubmission
                    }
                    shouldMatchPreviousPhasedRelease={shouldMatchPreviousPhasedRelease}
                    open={submitInProgressModalVisible}
                    selectedBuildNumbers={selectedBuildNumbers}
                    onSuccess={() => setSubmitInProgressModalVisible(false)}
                    onCancel={() => setSubmitInProgressModalVisible(false)}
                    reviewSubmissionWithoutAppVersion={stepData.reviewSubmissionWithoutAppVersion}
                />
            )}
        </Layout>
    );
};

export const titleForState = (
    isSubmitted: boolean,
    appStoreType: AppStoreType,
    status: ReleaseStepStatus,
    appStoreState: AppStoreState | undefined,
    isReleaseFromScraper: boolean,
    hasSelectedBuild: boolean,
) => {
    let submittedText: string;
    switch (appStoreType) {
        case AppStoreType.Apple:
            submittedText = 'Submitted to Apple';
            break;
        case AppStoreType.Google:
            submittedText = 'Submitted to Google';
            break;
        case AppStoreType.Amazon:
            submittedText = 'Submitted to Amazon';
            break;
        case AppStoreType.Huawei:
            submittedText = 'Submitted to Huawei';
            break;
        case AppStoreType.Samsung:
            submittedText = 'Submitted to Samsung';
            break;
    }

    if (isReleaseFromScraper) {
        return submittedText;
    }

    const appStoreTitle = appStoreTitleForState(appStoreType, appStoreState);

    switch (status) {
        case ReleaseStepStatus.Skipped:
            return 'Skipped';
        case ReleaseStepStatus.Inactive:
            return appStoreTitle || 'Not ready to submit';
        case ReleaseStepStatus.Blocked:
            return 'Submission blocked';
        case ReleaseStepStatus.Pending: {
            let readyToSubmitText: string;
            switch (appStoreType) {
                case AppStoreType.Apple:
                    readyToSubmitText = 'Ready to submit to Apple';
                    break;
                case AppStoreType.Google:
                    readyToSubmitText = 'Ready to submit to Google';
                    break;
                case AppStoreType.Amazon:
                    readyToSubmitText = 'Ready to submit to Amazon';
                    break;
                case AppStoreType.Huawei:
                    readyToSubmitText = 'Ready to submit to Huawei';
                    break;
                case AppStoreType.Samsung:
                    readyToSubmitText = 'Ready to submit to Samsung';
                    break;
            }
            return (
                isSubmitted ? submittedText
                : hasSelectedBuild ? readyToSubmitText
                : 'Select a build'
            );
        }
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Ready:
            return submittedText;
    }
};

const descriptionForState = (
    isSubmitted: boolean,
    isSubmittedByRunway: boolean,
    willBeSubmittedByRunway: boolean,
    autoSubmitStrategy: AutoSubmitStrategy | undefined,
    status: ReleaseStepStatus,
    isReleaseFromScraper: boolean,
    isRollback: boolean,
    selectedBuildNumbers: string[],
    isAppBuildSubmissionBlocked: boolean,
    displayableVersion: string,
) => {
    const submittedText =
        isSubmittedByRunway ?
            `Your update has been automatically submitted for review by Runway.`
        :   'Your update has been submitted for review.';
    let runwayWillSubmitText = `Runway will automatically submit your selected build for review on your target submit date if all previous steps are green.`;
    if (autoSubmitStrategy) {
        switch (autoSubmitStrategy) {
            case AutoSubmitStrategy.OnceStepsComplete:
                runwayWillSubmitText = `Runway will automatically submit your selected build for review once all previous steps are green.`;
                break;
            case AutoSubmitStrategy.OnceStepsCompleteAfterTargetDate:
                runwayWillSubmitText = `Runway will automatically submit your selected build for review once all previous steps are green and no sooner than the configured target date.`;
                break;
            case AutoSubmitStrategy.AfterTargetDateEvenIfNotReady:
                runwayWillSubmitText = `Runway will automatically submit your selected build for review no sooner than the configured target date even if all previous steps are not green.`;
                break;
            case AutoSubmitStrategy.AsSoonAsPossible:
                runwayWillSubmitText = `Runway will automatically submit your selected build for review as soon as possible.`;
                break;
        }
    }

    if (isReleaseFromScraper) {
        return submittedText;
    }

    const hasSelectedBuilds = selectedBuildNumbers.length > 0;

    switch (status) {
        case ReleaseStepStatus.Blocked:
            if (isAppBuildSubmissionBlocked) {
                return `Your ${displayableVersion} submission is blocked. Please resolve issues to proceed with your submission.`;
            }
            return 'Select a build and submit your app for review.';
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return willBeSubmittedByRunway ? runwayWillSubmitText : 'Select a build and submit your app for review.';
        case ReleaseStepStatus.Pending: {
            if (isRollback) {
                let build = '';
                if (selectedBuildNumbers.length > 0) {
                    build = `(${selectedBuildNumbers[0]}) `; // intentional trailing whitespace
                }
                return `Your rollback ${build}is ready to be submitted for review.`;
            }
            const submitForReviewText =
                willBeSubmittedByRunway ? runwayWillSubmitText
                : hasSelectedBuilds ? 'Your app version can now be submitted for review.'
                : 'Select a build and submit your app for review.';
            return isSubmitted ? submittedText : submitForReviewText;
        }
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Ready:
            return submittedText;
    }
};

const appStoreButtonText = (appStoreType: AppStoreType) => {
    const consoleName = displayConsoleNameForAppStoreType(appStoreType);
    return `${consoleName}`;
};

const submitText = (appStoreSelectedBuilds?: AppStoreAvailableBuild[]) => {
    if (appStoreSelectedBuilds) {
        if (appStoreSelectedBuilds.length > 1) {
            const buildNumbers = appStoreSelectedBuilds.map((build) => `#${build.appStoreBuild.buildNumber}`);
            return `Confirm & submit builds ${buildNumbers.join(', ')} for review`;
        } else if (appStoreSelectedBuilds.length > 0) {
            const appStoreSelectedBuild = appStoreSelectedBuilds[0];

            return `Confirm & submit build #${appStoreSelectedBuild?.appStoreBuild.buildNumber} for review`;
        }
    }

    return 'Confirm & submit update for review';
};

export const statusColorForReleaseStepStatus: (
    stepStatus: ReleaseStepStatus,
    isReleaseFromScraper: boolean,
) => string = (stepStatus, isReleaseFromScraper) => {
    if (isReleaseFromScraper) {
        return runwayColors.statusGrey;
    }

    return innerStepStatusColorForReleaseStepStatus(stepStatus);
};
